<template>
  <div class="campaign">
    <div class="monthly-limit-bar" v-if="group.currentCampaignCount !== undefined">
      <div class="caption">{{ currentMonth }}月の上限数</div>
      <div v-if="group.allowWebInstantwin && isBeforeNttAaaSwitchover">
        WEBインスタントウィン同時開催数: {{ formatNumber(group.currentWebInstantwinCount)
        }}<span v-if="isUnlimitedWebInstantwin">&nbsp;/ {{ formatNumber(group.maxWebInstantwinCount) }}</span>
      </div>
      <div v-if="group.allowInInstantwin">
        Instagram インスタントウィン同時開催数: {{ formatNumber(group.currentInInstantwinCount)
        }}<span v-if="isUnlimitedInInstantwin">&nbsp;/ {{ formatNumber(group.maxInInstantwinCount) }}</span>
      </div>
      <div>
        キャンペーン同時開催数: {{ formatNumber(group.currentCampaignCount)
        }}<span v-if="isUnlimitedCampaign">&nbsp;/ {{ formatNumber(group.maxCampaignCount) }}</span>
      </div>
      <div>
        応募者数: {{ formatNumber(group.currentMonthlyApplicantCount)
        }}<span v-if="isUnlimitedMonthlyApplicant">&nbsp;/ {{ formatNumber(group.maxMonthlyApplicantCount) }}</span>
      </div>
      <div>
        DM配信数: {{ formatNumber(group.currentMonthlyDmCount)
        }}<span v-if="isUnlimitedMonthlyDm">&nbsp;/ {{ formatNumber(group.maxMonthlyDmCount) }}</span>
      </div>
      <div>
        <el-tooltip placement="bottom">
          <div slot="content">
            月間で送れるDMの配信件数です。 <br />
            この件数に、インスタントウィンは含まれません。
          </div>
          <i class="mdi mdi-help-circle"></i>
        </el-tooltip>
      </div>
    </div>
    <div class="message message-warning mb-x3" v-if="!groups.length && !guestGroups.length">
      <div>グループに所属していないため、データを表示できません。</div>
      <div>ご利用される方は、右下のサポートチャットよりお問い合わせください。</div>
    </div>
    <div class="message message-warning mb-x3" v-if="contractStatus === 3">
      <div>現在、サービスのご利用が終了しているため、一部の機能に制限がかかっています。</div>
      <div>ご利用を再開したい場合は、弊社担当にご連絡いただくか、右下のチャットからお問い合わせください。</div>
    </div>
    <div class="card">
      <el-tabs v-model="activeTab" class="campaign-tabs" @tab-click="handleTabClick">
        <el-tab-pane label="キャンペーン一覧" name="campaign-list">
          <template v-if="firstLoaded">
            <div class="campaign-column">
              <div class="campaign-head">
                <div class="campaign-head-actions">
                  <el-button type="primary" @click="openCampaignCreateDialog" :disabled="contractStatus === 3"
                    ><i class="mdi mdi-plus"></i>キャンペーンの新規作成</el-button
                  >
                  <el-button v-if="isAddInstantwin" type="primary" @click="openInstantwinCreateDialog"
                    ><i class="mdi mdi-plus"></i>インスタントウィンの新規作成</el-button
                  >
                  <el-button
                    v-if="isUserAdmin"
                    type="default"
                    @click="openAccountCreateCampaignSetting"
                    :disabled="contractStatus === 3"
                    ><i class="mdi mdi-settings"></i>キャンペーン開催アカウントの管理</el-button
                  >
                </div>
                <csv-download
                  v-if="!error && campaigns.length"
                  :download="downloadCsv"
                  :disabled="contractStatus === 3"
                ></csv-download>
              </div>
              <div class="campaign-filter-row">
                <campaign-filter />
              </div>
              <div v-if="error">
                <div class="message message-error">
                  データ取得に失敗しました。恐れ入りますが、時間を置いて再度お試しください。
                </div>
              </div>
              <div v-else>
                <div class="message message-info" v-if="!campaigns.length && isFiltered">
                  条件に該当するキャンペーンのデータがありません。
                </div>
                <div class="message message-info" v-else-if="!campaigns.length">
                  キャンペーンのデータがありません。キャンペーンを作成すると、ここに一覧表示されます。
                </div>
                <div v-else>
                  <CampaignUnauthenticatedAccount
                    v-if="isBeforeNttAaaSwitchover"
                    class="mb-x2"
                    @campaignUpdated="onCampaignUpdated"
                  />
                  <CampaignUrlSettingAlert
                    v-if="showUrlSettingAlert"
                    class="mb-x2"
                    :items="urlSettingAlertItems"
                    @help="onUrlSettingAlertHelpClick"
                    @edit="onUrlSettingAlertEditClick"
                  />
                  <campaign-table
                    v-if="campaigns.length"
                    ref="campaignTable"
                    :campaigns="campaigns"
                    :options="options"
                    :onChangePagingNo="onChangePagingNo"
                    :onChangeLimitCount="onChangeLimitCount"
                    :onChangeSortBy="onChangeSortBy"
                    @campaignUpdated="onCampaignUpdated"
                  ></campaign-table>
                </div>
              </div>
            </div>
          </template>
        </el-tab-pane>
        <el-tab-pane v-if="allowBenchmarkAnalytics" label="競合キャンペーン分析" name="twitter_benchmark_analytics" />
      </el-tabs>
    </div>
    <DialogCampaignCreate ref="DialogCampaignCreate" @campaignUpdated="onCampaignUpdated"></DialogCampaignCreate>
    <CampaignAddEditDialog
      v-if="isAddInstantwin"
      ref="CampaignAddEditDialog"
      @actionSuccess="onCampaignUpdated"
    ></CampaignAddEditDialog>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import 'moment-timezone'
import type { TabPane } from 'element-ui'
import moment from 'moment'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import CsvDownload from '@/client/components/_atoms/csv_download'
import CampaignUrlSettingAlert from '@/client/components/_molecules/campaign_url_setting_alert'
import { CampaignUnauthenticatedAccount } from '@/client/components/_organisms'
import DialogCampaignCreate from '@/client/components/_organisms/DialogCampaignCreate.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import api from '@/client/core/api'
import util from '@/client/core/util'
import CampaignAddEditDialog from '@/client/features/campaigns/components/campaign_add_edit_dialog'
import CampaignFilter from '@/client/features/campaigns/components/campaign_filter'
import CampaignTable from '@/client/features/campaigns/components/campaign_table'
import { filterUnsetUrlSettingCampaigns } from '@/client/features/campaigns/utils/campaignUrlSettingTarget'
import { formatDatetime, formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useCampaignStore } from '@/client/store/useCampaignStore'
import { useNavbarStore } from '@/client/store/useNavbarStore'
import { useSystemStore } from '@/client/store/useSystemStore'
import { formatEntryActionType } from '@/client/utils/campaign_entry_action_label'
import {
  hasSearchConditions,
  queryParamsToApiParams,
  validateAndCorrectQueryParams
} from '@/client/utils/campaign_query_params'
import { isBeforeNttAaaSwitchover } from '@/common/utils'
import { TCampaign } from '@/typings/api/campaigns.type'

export default defineComponent({
  components: {
    CampaignTable,
    CsvDownload,
    CampaignAddEditDialog,
    DialogCampaignCreate,
    CampaignUnauthenticatedAccount,
    CampaignUrlSettingAlert,
    CampaignFilter
  },
  data() {
    return {
      isRedirecting: false,
      activeTab: 'campaign-list'
    }
  },
  async created() {
    this.setBreadcrumb([
      {
        name: 'キャンペーン一覧',
        link: null
      }
    ])

    if (this.guestGroups.some(v => v.id === this.currentGroupId)) {
      this.$router.push('/settings/group#accounts')
      return
    }

    if (this.currentGroupId) {
      this.fetchCampaignsFromQuery()
    }
  },
  computed: {
    ...mapState(useSystemStore, {
      currentGroupId: state => state.currentGroup?.id ?? null,
      contractStatus: state => state.currentGroup?.contractStatus,
      group: state => state.currentGroup,
      groups: state => state.groups,
      allowInstantwin: state => state.currentGroup?.allowInstantwin,
      allowCreateInstantwin: state => state.currentGroup?.allowCreateInstantwin,
      allowBenchmarkAnalytics: state => state.currentGroup?.allowBenchmarkAnalytics,
      isUserAdmin: state => state.currentGroup?.isUserAdmin,
      isSystemAdmin: state => Boolean(state.user?.isSystemAdmin),
      guestGroups: state => state.guestGroups
    }),

    ...mapState(useCampaignStore, {
      campaigns: state => {
        return state.campaigns.map(campaign => {
          if (!campaign.account) {
            campaign.account = {
              name: '削除されたアカウント'
            }
          }
          return campaign
        })
      },
      total: state => state.total,
      loading: state => state.loading,
      firstLoaded: state => state.firstLoaded,
      error: state => state.error
    }),

    isBeforeNttAaaSwitchover,

    currentApiParams() {
      return queryParamsToApiParams(this.$route.query, this.currentGroupId, this.total)
    },

    /**
     * URL 設定催促アラートの表示対象
     * （開催開始後かつ終了日時が NTT AAA の切替日時以降で URL 未設定のリポスト/引用系キャンペーン）
     */
    unsetUrlSettingCampaigns(): TCampaign[] {
      return filterUnsetUrlSettingCampaigns(this.campaigns)
    },

    /**
     * URL 設定催促アラートを表示するか
     */
    showUrlSettingAlert(): boolean {
      return this.unsetUrlSettingCampaigns.length > 0
    },

    /**
     * URL 設定催促アラートに表示する対象キャンペーン（表示対象の全件）
     */
    urlSettingAlertItems(): {
      id: number
      campaignName: string
      account: { name: string; image?: string; symbol: 'X' }
    }[] {
      return this.unsetUrlSettingCampaigns.map(campaign => ({
        id: campaign.id,
        campaignName: campaign.title ?? '',
        account: {
          name: campaign.account?.name ?? '',
          image: campaign.account?.pictureUrl,
          symbol: 'X'
        }
      }))
    },

    options() {
      return {
        total: this.total,
        pagingNo: this.currentApiParams.pagingNo,
        limitCount: this.currentApiParams.limitCount,
        sortBy: this.currentApiParams.sortBy
      }
    },

    isFiltered() {
      return hasSearchConditions(this.$route.query)
    },

    isUnlimitedInstantwin() {
      return this.group.maxInstantwinCount !== 0
    },

    isUnlimitedWebInstantwin() {
      return this.group.maxWebInstantwinCount !== 0
    },

    isUnlimitedInInstantwin() {
      return this.group.maxInInstantwinCount !== 0
    },

    isUnlimitedCampaign() {
      return this.group.maxCampaignCount !== 0
    },

    isUnlimitedMonthlyApplicant() {
      return this.group.maxMonthlyApplicantCount !== 0
    },

    isUnlimitedMonthlyDm() {
      return this.group.maxMonthlyDmCount !== 0
    },

    isTwitterInstantwinStopped() {
      const cutoff = moment.tz('2025-11-01 00:00:00', 'Asia/Tokyo')
      return moment().tz('Asia/Tokyo').isSameOrAfter(cutoff)
    },

    isAddInstantwin() {
      if (this.isTwitterInstantwinStopped) {
        return false
      }

      return this.allowInstantwin && (this.isSystemAdmin || this.allowCreateInstantwin)
    },

    currentMonth() {
      return moment().format('M')
    }
  },
  watch: {
    currentGroupId() {
      if (this.currentGroupId) {
        if (this.$route.path === '/campaigns') {
          if (Object.keys(this.$route.query).length > 0) {
            this.$router.replace({ path: '/campaigns' })
          } else {
            this.fetchCampaignsFromQuery()
          }
        } else {
          this.$router.push({ path: '/campaigns' })
        }
      }
    },
    '$route.query': {
      handler() {
        if (this.currentGroupId && !this.isRedirecting) {
          this.fetchCampaignsFromQuery()
        }
      },
      deep: true
    }
  },
  methods: {
    formatNumber,
    ...mapActions(useNavbarStore, ['setBreadcrumb']),
    ...mapActions(useCampaignStore, ['reset', 'fetchCampaignsWithParams', 'setLastQueryParams']),

    /**
     * タブ選択で競合キャンペーン分析ページへ遷移する。
     */
    handleTabClick(tab: TabPane) {
      if (tab.name === 'twitter_benchmark_analytics') {
        this.$router.push('/twitter_benchmark_analytics')
      }
    },

    /**
     * クエリパラメータからキャンペーンデータを取得
     */
    async fetchCampaignsFromQuery() {
      const { correctedQuery, hasErrors } = validateAndCorrectQueryParams(this.$route.query, this.total)

      if (hasErrors) {
        this.isRedirecting = true
        this.$router
          .replace({ query: correctedQuery })
          .then(() => {
            this.$nextTick(() => {
              this.isRedirecting = false
              if (this.currentGroupId) {
                this.fetchCampaignsFromQuery()
              }
            })
          })
          .catch(() => {
            this.isRedirecting = false
          })
        return
      }

      const apiParams = this.currentApiParams
      await this.fetchCampaignsWithParams(apiParams)

      const shouldRedirect =
        this.total > 0 ? apiParams.pagingNo > Math.ceil(this.total / apiParams.limitCount) : apiParams.pagingNo > 1

      if (shouldRedirect) {
        this.isRedirecting = true
        this.updateQueryParams({ page: undefined })
        this.$nextTick(() => {
          this.isRedirecting = false
          if (this.currentGroupId) {
            this.fetchCampaignsFromQuery()
          }
        })
        return
      }

      this.setLastQueryParams(this.$route.query)
    },

    /**
     * キャンペーン更新イベントのハンドラ
     */
    onCampaignUpdated() {
      if (this.currentGroupId) {
        this.fetchCampaignsFromQuery()
      }
    },

    /**
     * URL 設定催促アラートのヘルプリンククリック
     */
    onUrlSettingAlertHelpClick() {
      TrackingService.sendEvent('click:キャンペーン一覧|応募収集URL設定ヘルプ')
    },

    /**
     * URL 設定催促アラートのリンクアイコンクリック：対象キャンペーンの URL 設定ダイアログを開く
     */
    onUrlSettingAlertEditClick(campaignId: number) {
      const target = this.unsetUrlSettingCampaigns.find(campaign => campaign.id === campaignId)
      if (!target) return
      ;(this.$refs.campaignTable as { openTweetPostDialog: (campaign: TCampaign) => void }).openTweetPostDialog(target)
    },

    /**
     * クエリパラメータを更新してページ遷移
     */
    updateQueryParams(newParams) {
      const currentQuery = { ...this.$route.query }
      const updatedQuery = { ...currentQuery, ...newParams }

      Object.keys(updatedQuery).forEach(key => {
        if (updatedQuery[key] === undefined || updatedQuery[key] === '') {
          delete updatedQuery[key]
        }
      })

      this.setLastQueryParams(updatedQuery)
      this.$router.push({ query: updatedQuery })
    },

    /**
     * ページを変更イベント（変更後は先頭までスクロールする）
     */
    onChangePagingNo(page: number) {
      this.updateQueryParams({ page: page === 1 ? undefined : page.toString() })
      util.scrollToTop()
    },

    /**
     * リミット数を変更イベント（ページはリセットする）
     */
    onChangeLimitCount(limitCount: number) {
      this.updateQueryParams({
        limit: limitCount === 25 ? undefined : limitCount.toString(),
        page: undefined
      })
    },

    /**
     * ソートを変更イベント（ページはリセットする）
     */
    onChangeSortBy(sortBy: string) {
      this.updateQueryParams({
        sortBy: sortBy === '-status' ? undefined : sortBy,
        page: undefined
      })
    },

    /**
     * キャンペーンの新規作成ダイアログを開く
     */
    openCampaignCreateDialog() {
      TrackingService.sendEvent('click:キャンペーン一覧|キャンペーンの新規作成')

      this.$refs.DialogCampaignCreate.open()
    },

    openInstantwinCreateDialog() {
      if (this.isTwitterInstantwinStopped) {
        return
      }

      TrackingService.sendEvent('click:キャンペーン一覧|インスタントウィンの新規作成')

      this.$refs.CampaignAddEditDialog.open('addTwitter', null, 'instantwin')
    },

    /**
     * キャンペーン開催アカウントの管理
     */
    openAccountCreateCampaignSetting() {
      TrackingService.sendEvent('click:キャンペーン一覧|キャンペーン開催アカウントの管理')

      this.$router.push('/settings/group')
    },

    /**
     * CSVダウンロード
     */
    async downloadCsv() {
      TrackingService.sendEvent('click:キャンペーン一覧|CSVダウンロード')

      const params = Object.assign({}, this.currentApiParams, {
        limitCount: -1,
        forCsv: 1
      })
      const result = await api.get('/campaigns', {
        params
      })
      if (!result.data) {
        return {
          error: 'ERROR_LOADING'
        }
      }
      const campaigns = result.data.campaigns
      const filename = `campaigns_${moment().format('YYYY_MM_DD')}`
      const csvData = []
      csvData.push([
        'ステータス',
        'タイトル',
        '応募条件',
        '開始日時',
        '終了日時',
        '応募者数',
        '当選者数',
        '当選者数(残り)',
        'フォロワー増減数',
        'キャンペーンメモ'
      ])

      campaigns.forEach(campaign => {
        let status = ''

        switch (campaign.status) {
          case CAMPAIGN_STATUS.BEFORE_EVENT:
            status = '開催前'
            break
          case CAMPAIGN_STATUS.BEFORE_COLLECTION:
            status = '開催中(応募収集待ち)'
            break
          case CAMPAIGN_STATUS.DURING_COLLECTION:
            status = '開催中(応募収集中)'
            break
          case CAMPAIGN_STATUS.DURING_FINAL_COLLECTION:
            status = '開催終了(最終集計中)'
            break
          case CAMPAIGN_STATUS.COLLECTION_COMPLETED:
            status = '開催終了(全データ集計完了)'
            break
        }

        csvData.push([
          status,
          campaign.title,
          formatEntryActionType(campaign.targetSns, campaign.type, campaign.entryActionType),
          formatDatetime(campaign.startDatetime),
          formatDatetime(campaign.endDatetime),
          campaign.applicantsCount,
          campaign.maxWinnerCount,
          campaign.maxWinnerCount - campaign.winnerCount,
          campaign.followerUpDown,
          campaign.memo ?? ''
        ])
      })
      return {
        filename,
        data: csvData
      }
    }
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    this.reset()
    next()
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

:deep(.campaign-tabs) {
  .el-tabs__nav-wrap {
    display: inline-block;
  }
}

.campaign-column {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit * 2;
}

.monthly-limit-bar {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-unit * 2;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: $spacing-unit * 2;
}

.campaign-head {
  display: flex;
  gap: $spacing-unit * 2;
  align-items: center;
  justify-content: space-between;
}

.campaign-head-actions {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
}

.campaign-filter-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
</style>
